/**
 * Lineup (spec page 5, approved): every open request past New, ranked.
 * Impact x Urgency sets the default order; dragging pins a manual rank
 * (shown with a pin). "Re-sort by score" clears pins. Baker and Robin both
 * reorder. Cumulative hours + a line where a week of capacity runs out.
 * Reordering is only enabled with no filters active (a filtered drag would
 * scramble the global order).
 */
import { useState } from 'react';
import {
  DndContext, PointerSensor, closestCenter, useSensor, useSensors, type DragEndEvent,
} from '@dnd-kit/core';
import { SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import type { Settings } from '../config';
import { writeRanks, type Me, type Req } from '../lib/data';
import { lineupOrder, reorder, resortByScore } from '../lib/order';
import { applyFilter, Filters, isFiltered, NO_FILTER, type Filter } from '../parts';
import { Divisions, Empty, StatusTag, TierBadge } from '../ui';
import { Guide, Paused } from '../guide';

export function Lineup({ me, items, settings, go }: { me: Me; items: Req[]; settings: Settings; go: (to: string) => void }) {
  const [filter, setFilter] = useState<Filter>(NO_FILTER);
  const [err, setErr] = useState<string | null>(null);
  const [confirmResort, setConfirmResort] = useState(false);
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));
  void me;

  const eligible = items.filter((r) => r.status !== 'new' && r.status !== 'completed');
  const ordered = lineupOrder(eligible);
  const rows = applyFilter(ordered, filter);
  const canDrag = !isFiltered(filter);
  const pins = eligible.filter((r) => r.rankManual).length;

  async function onDragEnd(e: DragEndEvent) {
    if (!e.over || e.active.id === e.over.id) return;
    const to = ordered.findIndex((r) => r.id === e.over!.id);
    const changes = reorder(ordered, String(e.active.id), to);
    if (!changes.length) return;
    setErr(null);
    try { await writeRanks(changes); } catch { setErr('Someone else changed the lineup at the same moment - it has refreshed, try the move again.'); }
  }

  async function resort() {
    setConfirmResort(false); setErr(null);
    const changes = resortByScore(eligible);
    if (!changes.length) return;
    try { await writeRanks(changes); } catch { setErr('The lineup changed while re-sorting - try again.'); }
  }

  let cum = 0;
  let lineDrawn = false;

  return (
    <div className="card">
      <div className="toolbar">
        <h2 className="page-title">Lineup <span className="count">{rows.length}</span></h2>
        <Filters items={eligible} value={filter} onChange={setFilter} />
        <span className="spacer" />
        {confirmResort ? (
          <>
            <span className="muted small">Re-sort by score{pins ? ` and clear ${pins} hand-placed pin${pins > 1 ? 's' : ''}` : ''}?</span>
            <button onClick={resort}>Yes, re-sort</button>
            <button className="secondary" onClick={() => setConfirmResort(false)}>Cancel</button>
          </>
        ) : <button className="secondary" onClick={() => setConfirmResort(true)}>Re-sort by score</button>}
      </div>
      <Guide page="lineup" />
      {!canDrag && <Paused>reordering is off while a filter is on (a filtered drag would scramble the full order). Clear the filters to drag rows.</Paused>}
      {err && <p className="err">{err}</p>}
      {!rows.length && <Empty>Nothing in the lineup yet - requests join once they're past New (reviewed).</Empty>}
      <div className="lineup-head">
        <span>#</span><span>Request</span><span>Divisions</span><span>Score</span><span>Hrs</span><span>Stage</span><span>Cum.</span>
      </div>
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
        <SortableContext items={rows.map((r) => r.id)} strategy={verticalListSortingStrategy}>
          {rows.map((r) => {
            cum += r.estHours ?? 0;
            const showLine = !lineDrawn && cum > settings.capacityWeek;
            if (showLine) lineDrawn = true;
            return (
              <div key={r.id}>
                {showLine && <div className="capacity-line">about one week of capacity ({settings.capacityWeek}h)</div>}
                <Row r={r} n={ordered.indexOf(r) + 1} cum={cum} settings={settings} disabled={!canDrag} onOpen={() => go(`/r/${r.id}`)} />
              </div>
            );
          })}
        </SortableContext>
      </DndContext>
      {rows.some((r) => r.estHours == null) && <p className="muted small">Rows without estimated hours don't count toward the capacity line.</p>}
    </div>
  );
}

function Row({ r, n, cum, settings, disabled, onOpen }: {
  r: Req; n: number; cum: number; settings: Settings; disabled: boolean; onOpen: () => void;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: r.id, disabled });
  const style = { transform: CSS.Transform.toString(transform), transition };
  return (
    <div ref={setNodeRef} style={style} className={`lineup-row ${isDragging ? 'dragging' : ''}`}>
      <span className="grip" {...attributes} {...listeners} title={disabled ? '' : 'Drag to reorder'}>{disabled ? n : <>&#8942;&#8942; {n}</>}</span>
      <span className="lr-title" onClick={onOpen}>
        {r.title}{r.rankManual && <span className="pin" title="Placed by hand">pinned</span>}
        {r.waitingOn && <span className="waiting">waiting: {r.waitingOn}</span>}
      </span>
      <span><Divisions ids={r.divisions} /></span>
      <span><TierBadge r={r} settings={settings} /></span>
      <span className="num">{r.estHours ?? '-'}</span>
      <span><StatusTag status={r.status} /></span>
      <span className="num">{cum}h</span>
    </div>
  );
}
